import useFormat from "hooks/useFormat";
import React from "react";
import { defineMessage, useIntl } from "react-intl";
import { useColorScaleVDP, VerticalDotPlotAuto } from "../charts-motion";
import { withFigureIO } from "../components/figure";
import { useReadScrolly } from "../components/scrolly";
import { useConfig } from "../config";
import { metadata } from "../data/data_learning_fig_LITBYYRS";
import { datasets } from "../data/data_learning_fig_LITBYYRS_datasets";
import { countryIdIso, getCountryMeta, mkGemDataDecoder, useNamedCountries } from "../domain";
import { useMedia } from "../hooks";
import { unsafeFromArray } from "../lib";
import * as M from "../materials";
import { breakpoints } from "../materials/consts.stylex";
import { An, Ar, identity, io, O, Ord, pipe } from "../prelude";
export * from "../data/data_learning_fig_LITBYYRS";
const statusOrder = {
    "With up to 4 years of schooling": 1,
    "With 5-6 years of schooling": 2
};
const Status = io.keyof(statusOrder);
const ordStatus = Ord.contramap((x)=>statusOrder[x])(Ord.ordNumber);
const statusLookup = {
    "With up to 4 years of schooling": defineMessage({
        id: "fig.learning.LITBYYRS.schooling0to4",
        defaultMessage: "With up to 4 years of schooling"
    }),
    "With 5-6 years of schooling": defineMessage({
        id: "fig.learning.LITBYYRS.schooling5to6",
        defaultMessage: "With 5–6 years of schooling"
    })
};
export const Data = mkGemDataDecoder([
    "iso3c",
    "ind_id"
], {
    value: io.number,
    status: Status
});
export const Chart = ({ data: rawData })=>{
    const intl = useIntl();
    const env = useConfig();
    const screenMDown = !useMedia(breakpoints.m);
    const { activeSection } = useReadScrolly();
    const highlightItems = pipe(activeSection, O.map((x)=>x.split(",")), O.chain((strIds)=>Ar.array.sequence(O.option)(strIds.map((strId)=>getCountryMeta(env.countries, countryIdIso.wrap(strId))))), O.map((xs)=>xs.map((meta)=>meta.country)), O.chain((xs)=>An.fromArray(xs)));
    const data_ = useNamedCountries(rawData);
    const data = React.useMemo(()=>{
        return data_.map((x)=>({
                ...x,
                status_name: intl.formatMessage(statusLookup[x.status])
            }));
    }, [
        intl,
        data_
    ]);
    const chartData = React.useMemo(()=>{
        return unsafeFromArray(Ar.sortBy([
            Ord.contramap((x)=>x.status)(ordStatus),
            Ord.contramap((x)=>x.value)(Ord.ordNumber)
        ])(data));
    }, [
        data
    ]);
    const getX = React.useCallback((x)=>x.country_name, []);
    const getValue = React.useCallback((x)=>x.value, []);
    const getColor = React.useCallback((x)=>x.status_name, []);
    const getColorPalette = React.useCallback(()=>M.fromCount(M.colorRanges.diverging, 2), []);
    const formatY = useFormat(".0%");
    const [colorScale, colorLegendValues] = useColorScaleVDP({
        data: chartData,
        getColor,
        colorPalette: getColorPalette,
        formatColor: identity
    });
    return <VerticalDotPlotAuto height={screenMDown ? M.chartHeight.s : M.chartHeight.m} data={chartData} getX={getX} getValue={getValue} getColor={getColor} formatX={identity} formatValue={formatY} domainY={[
        0,
        1
    ]} colorScale={colorScale} colorLegendValues={colorLegendValues} showTooltip showLegend showYExtent={O.isNone(highlightItems)} dotStyle={"small"} labelStyleX="none" highlightItems={highlightItems}/>;
};
export default withFigureIO({
    url: new URL("../data/data_learning_fig_LITBYYRS.json", import.meta.url).href,
    csv: new URL("../data/data_learning_fig_LITBYYRS.zip", import.meta.url).href,
    xlsx: new URL("../data/data_learning_fig_LITBYYRS.xlsx", import.meta.url).href,
    metadata,
    Data,
    Chart,
    datasets
});
